import React, { useEffect, useState } from 'react';
import { useBranchAbsencePolicyQuery, useSetBranchAbsencePolicyMutation } from '../../hooks/useBranchAbsencePolicy';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import s from './BranchAbsencePolicyModal.module.css';

/**
 * AB#2024 — per-branch "Break & absence rules" editor. Opens from each branch
 * card. Edits the caps enforced server-side by SetStaffAbsenceCommand: how many
 * breaks/day, total break minutes/day, the longest single break, and whether an
 * owner may override the caps. Defaults (1 / 60 / 60 / on) are seeded server-side.
 */
export interface BranchAbsencePolicyModalProps {
    branch: { id: number; name: string };
    onClose: () => void;
}

const BranchAbsencePolicyModal: React.FC<BranchAbsencePolicyModalProps> = ({ branch, onClose }) => {
    const { t } = useLanguage();
    const toast = useToast();
    const { data: policy, isLoading } = useBranchAbsencePolicyQuery(branch.id);
    const saveMut = useSetBranchAbsencePolicyMutation(branch.id);

    const [maxBreaks, setMaxBreaks] = useState(1);
    const [maxTotal, setMaxTotal] = useState(60);
    const [maxSingle, setMaxSingle] = useState(60);
    const [allowOverride, setAllowOverride] = useState(true);

    useEffect(() => {
        if (policy) {
            setMaxBreaks(policy.maxBreaksPerDay);
            setMaxTotal(policy.maxBreakMinutesPerDay);
            setMaxSingle(policy.maxSingleBreakMinutes);
            setAllowOverride(policy.allowOwnerOverride);
        }
    }, [policy]);

    const save = () => {
        saveMut.mutate(
            {
                branchId: branch.id,
                maxBreaksPerDay: maxBreaks,
                maxBreakMinutesPerDay: maxTotal,
                maxSingleBreakMinutes: maxSingle,
                allowOwnerOverride: allowOverride,
            },
            {
                onSuccess: () => { toast.showToast(t('branch.absencePolicy.saved'), 'success'); onClose(); },
                onError: () => toast.showToast(t('branch.absencePolicy.saveFailed'), 'error'),
            },
        );
    };

    const numField = (label: string, help: string, value: number, set: (n: number) => void, min: number, max: number) => (
        <label className={s.field}>
            <span className={s.label}>{label}</span>
            <input
                type="number"
                min={min}
                max={max}
                value={value}
                onChange={(e) => set(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
            />
            <span className={s.help}>{help}</span>
        </label>
    );

    return (
        <div className={s.overlay} onMouseDown={onClose}>
            <div className={s.card} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className={s.header}>
                    <div>
                        <h2 className={s.title}>{t('branch.absencePolicy.title')}</h2>
                        <p className={s.subtitle}>{branch.name}</p>
                    </div>
                    <button type="button" className={s.close} aria-label={t('branch.absencePolicy.cancel')} onClick={onClose}>×</button>
                </div>

                <div className={s.body}>
                    {isLoading ? (
                        <p className={s.muted}>{t('branch.absencePolicy.loading')}</p>
                    ) : (
                        <>
                            {numField(t('branch.absencePolicy.maxBreaks'), t('branch.absencePolicy.maxBreaks.help'), maxBreaks, setMaxBreaks, 1, 20)}
                            {numField(t('branch.absencePolicy.maxTotal'), t('branch.absencePolicy.maxTotal.help'), maxTotal, setMaxTotal, 0, 480)}
                            {numField(t('branch.absencePolicy.maxSingle'), t('branch.absencePolicy.maxSingle.help'), maxSingle, setMaxSingle, 5, 480)}
                            <label className={`${s.field} ${s.checkField}`}>
                                <input type="checkbox" checked={allowOverride} onChange={(e) => setAllowOverride(e.target.checked)} />
                                <span>
                                    <span className={s.label}>{t('branch.absencePolicy.override')}</span>
                                    <span className={s.help}>{t('branch.absencePolicy.override.help')}</span>
                                </span>
                            </label>
                        </>
                    )}
                </div>

                <div className={s.footer}>
                    <button type="button" className={s.saveBtn} disabled={saveMut.isPending || isLoading} onClick={save}>
                        {t('branch.absencePolicy.save')}
                    </button>
                    <button type="button" className={s.cancelBtn} onClick={onClose}>
                        {t('branch.absencePolicy.cancel')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BranchAbsencePolicyModal;
